import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { wsService } from '../services/websocket';
import { isTokenExpired } from '../utils/jwtUtils';

export interface User {
  id: string;
  username: string;
  email?: string;
  walletAddress?: string;
  role: string;
  licenseExpiresAt?: Date;
}

interface AuthContextType {
  user: User | null;
  userRole: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Decode JWT token to extract user information
  const decodeToken = (token: string): User | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);

      return {
        id: payload.identity,
        username: payload.identity,
        email: payload.email,
        walletAddress: payload.walletAddress,
        role: payload.role || 'user',
        licenseExpiresAt: payload.licenseExpiresAt ? new Date(payload.licenseExpiresAt) : undefined,
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Check for existing token on mount and periodically
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Check if token is expired
        if (isTokenExpired(token)) {
          // Token expired, remove it and clear user
          localStorage.removeItem('authToken');
          setUser(null);
          // Store expiration message for login page
          sessionStorage.setItem('sessionExpired', 'true');
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        } else {
          const userData = decodeToken(token);
          setUser(userData);
        }
      } else {
        setUser(null);
      }
    };

    // Check immediately
    checkToken();
    setIsLoading(false);

    // Check periodically (every 30 seconds)
    const interval = setInterval(checkToken, 30000);

    return () => clearInterval(interval);
  }, []);

  const login = (token: string) => {
    localStorage.setItem('authToken', token);
    const userData = decodeToken(token);
    setUser(userData);
    // Reconnect WebSocket with new token to join licensed room
    wsService.reconnect();
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    // Reconnect WebSocket without token (will join unlicensed room)
    wsService.reconnect();
  };

  const value = {
    user,
    userRole: user?.role || null,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
